import { createContext, useContext, useState, useEffect, useCallback, ReactNode, ComponentType } from 'react';
import { useAuth } from './AuthContext';
import { DEFAULT_PERMISSIONS, FEATURES, RoleKey } from '../lib/permissions';

interface RolePermissions {
  superadmin: string[];
  admin: string[];
  auditor: string[];
  suppliant: string[];
  board_member: string[];
  resident: string[];
  subcontractor: string[];
  external: string[];
}

interface PermissionsState extends RolePermissions {
  _commhubId?: string;
}

interface PermissionsContextType {
  permissions: PermissionsState | null;
  loading: boolean;
  hasPermission: (permissionId: string) => boolean;
  canAccessRoute: (route: string) => boolean;
  getRolePermissions: (role: string) => string[];
  savePermissions: (newPermissions: Partial<RolePermissions>) => Promise<boolean>;
  refreshPermissions: () => Promise<void>;
}

interface PermissionsProviderProps {
  children: ReactNode;
}

const PermissionsContext = createContext<PermissionsContextType | null>(null);

// CommHub API for permissions
const COMMHUB_BASE = 'https://www.commhub.cloud/api';
const COMMHUB_API_KEY = 'GlHLVQpgdQaYcNoXxMoUKFTcUwXgZME_4RUZ6Z0AztA';

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<PermissionsState | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch permissions from database
  const fetchPermissions = useCallback(async () => {
    try {
      const response = await fetch(`${COMMHUB_BASE}/data/permissions/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': COMMHUB_API_KEY
        },
        body: JSON.stringify({ filter: { id: 'default' }, limit: 1 })
      });
      
      const data = await response.json();
      
      if (data.documents?.length > 0) {
        const permsData = data.documents[0].data;
        // Build permissions object from database
        const perms: PermissionsState = {
          superadmin: Object.keys(FEATURES), // Superadmin always has all
          admin: permsData.admin || DEFAULT_PERMISSIONS.admin,
          auditor: permsData.auditor || DEFAULT_PERMISSIONS.auditor,
          suppliant: permsData.suppliant || DEFAULT_PERMISSIONS.suppliant,
          board_member: permsData.board_member || DEFAULT_PERMISSIONS.board_member,
          resident: permsData.resident || DEFAULT_PERMISSIONS.resident,
          subcontractor: permsData.subcontractor || DEFAULT_PERMISSIONS.subcontractor,
          external: permsData.external || DEFAULT_PERMISSIONS.external,
          _commhubId: data.documents[0].id // Store for updates
        };
        setPermissions(perms);
      } else {
        // Use defaults if no custom permissions
        setPermissions({
          ...DEFAULT_PERMISSIONS,
          superadmin: Object.keys(FEATURES)
        } as PermissionsState);
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // Fallback to defaults
      setPermissions({
        ...DEFAULT_PERMISSIONS,
        superadmin: Object.keys(FEATURES)
      } as PermissionsState);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save permissions to database
  const savePermissions = async (newPermissions: Partial<RolePermissions>): Promise<boolean> => {
    try {
      const commhubId = permissions?._commhubId;
      
      if (commhubId) {
        // Update existing
        await fetch(`${COMMHUB_BASE}/data/permissions/${commhubId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': COMMHUB_API_KEY
          },
          body: JSON.stringify({
            admin: newPermissions.admin,
            auditor: newPermissions.auditor,
            suppliant: newPermissions.suppliant,
            board_member: newPermissions.board_member,
            resident: newPermissions.resident,
            subcontractor: newPermissions.subcontractor,
            external: newPermissions.external,
            updated_at: new Date().toISOString()
          })
        });
      }
      
      if (permissions) {
        setPermissions({ 
          ...permissions, 
          ...newPermissions, 
          _commhubId: commhubId 
        });
      }
      return true;
    } catch (error) {
      console.error('Failed to save permissions:', error);
      return false;
    }
  };

  // Check if current user has a specific permission
  const hasPermission = useCallback((permissionId: string): boolean => {
    if (!user?.role || !permissions) return false;
    
    // Superadmin and Admin always have all permissions
    if (user.role === 'superadmin' || user.role === 'admin') return true;
    
    const roleKey = user.role as keyof RolePermissions;
    const rolePermissions = permissions[roleKey] || [];
    return rolePermissions.includes(permissionId);
  }, [user?.role, permissions]);

  // Check if current user can access a route
  const canAccessRoute = useCallback((route: string): boolean => {
    if (!user?.role || !permissions) return false;
    
    // Superadmin and Admin can access everything
    if (user.role === 'superadmin' || user.role === 'admin') return true;
    
    // Map routes to permissions
    const routePermissions: Record<string, string> = {
      '/dashboard': 'dashboard',
      '/issues': 'issues_view',
      '/issues/new': 'issues_create',
      '/properties': 'properties',
      '/apartments': 'apartments',
      '/residents': 'users_view',
      '/orders': 'orders',
      '/suppliers': 'suppliers',
      '/email': 'email',
      '/service-messages': 'service_messages',
      '/settings': 'settings'
    };
    
    const permission = routePermissions[route];
    if (!permission) return true; // Unknown routes are accessible
    
    return hasPermission(permission);
  }, [user?.role, permissions, hasPermission]);

  // Get all permissions for a role
  const getRolePermissions = useCallback((role: string): string[] => {
    if (!permissions) return [];
    if (role === 'superadmin') return Object.keys(FEATURES);
    const roleKey = role as keyof RolePermissions;
    return permissions[roleKey] || [];
  }, [permissions]);

  // Load permissions on mount
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const value: PermissionsContextType = {
    permissions,
    loading,
    hasPermission,
    canAccessRoute,
    getRolePermissions,
    savePermissions,
    refreshPermissions: fetchPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): PermissionsContextType {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

// HOC for permission-protected components
export function withPermission<P extends object>(
  WrappedComponent: ComponentType<P>, 
  requiredPermission: string
) {
  return function PermissionWrapper(props: P) {
    const { hasPermission } = usePermissions();
    
    if (!hasPermission(requiredPermission)) {
      return (
        <div className="p-8 text-center">
          <h2 className="text-xl font-semibold text-slate-900">Åtkomst nekad</h2>
          <p className="text-slate-500 mt-2">Du har inte behörighet att se denna sida.</p>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
}
